import type {
  MyTenantsResponse,
  PlatformAccess,
  ProvisionTenantRequest,
  ProvisionTenantResult,
  TenantSummary,
} from '@warehouselogix/shared';
import { ME_ROUTES, PLATFORM_ROUTES } from '@warehouselogix/shared';
import { useMemo } from 'react';

import { useAdminHttp } from './useAdminHttp';

/**
 * Platform + "me" calls (tenancy v1). These need NO current tenant — they
 * are what the shell, the picker and the Tenants console use before (or
 * outside) a tenant is chosen. Tenant-scoped calls live in
 * {@link useAdminApi}, which requires a CurrentTenantProvider.
 */
export interface PlatformApi {
  /** `GET /platform/access` — fail-closed probe; 200 means platform admin. */
  getPlatformAccess: () => Promise<PlatformAccess>;
  /** `GET /platform/tenants` — all tenants (platform admins only). */
  listTenants: () => Promise<TenantSummary[]>;
  /** `POST /platform/tenants` — provision a tenant (defaults seeded, no members). */
  provisionTenant: (body: ProvisionTenantRequest) => Promise<ProvisionTenantResult>;
  /** `GET /me/tenants` — the caller's tenants + roles; performs first-sign-in binding. */
  myTenants: () => Promise<MyTenantsResponse>;
}

export function usePlatformApi(): PlatformApi {
  const http = useAdminHttp();

  return useMemo<PlatformApi>(
    () => ({
      getPlatformAccess: () => http<PlatformAccess>(PLATFORM_ROUTES.access),
      listTenants: () => http<TenantSummary[]>(PLATFORM_ROUTES.tenants),
      provisionTenant: (body) =>
        http<ProvisionTenantResult>(PLATFORM_ROUTES.tenants, { method: 'POST', body }),
      myTenants: () => http<MyTenantsResponse>(ME_ROUTES.tenants),
    }),
    [http]
  );
}
